import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Printer,
  Download,
  ArrowLeft,
  QrCode as QrCodeIcon,
  ChefHat,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  generate_qr_string,
  APL_LINK,
} from "../ServiceConnection/serviceconnection";
import html2canvas from "html2canvas";

export default function QrGenerate() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qrRef = useRef(null);
  const location = useLocation();

  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [qrScan, setQrImageSrc] = useState("");
  const [restroName, setRestroName] = useState(null);
  const [restroImg, setRestroImg] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [DeliveryUrl, setDeliveryUrl] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const { TableId, TableName } = location.state || {};

  // Handle print functionality
  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Handle download functionality - Captures entire QR card as PNG
  const handleDownload = async () => {
    if (!qrScan || !qrRef.current) {
      toast({
        title: "Error",
        description: "QR code not ready for download",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);

    try {
      // Capture the entire QR card element as canvas
      const canvas = await html2canvas(qrRef.current, {
        backgroundColor: null,
        scale: 2, // Higher resolution (2x)
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: qrRef.current.offsetWidth,
        height: qrRef.current.offsetHeight,
      });

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            throw new Error("Failed to create image");
          }

          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `QR-${restroName || "Restaurant"}-Table-${TableName}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          toast({
            title: "Success",
            description: "QR code downloaded successfully",
          });
        },
        "image/png",
        1.0, // Maximum quality
      );
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Error",
        description: "Failed to download QR code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Add event listener for Ctrl+P
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        handlePrint();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (TableId) {
      handleQr(TableId);
    }
  }, [TableId]);

  const handleQr = async (table_id) => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("table_id", table_id);

    try {
      const Response = await server_post_data(generate_qr_string, fd);
console.log(Response)
      if (Response.error) {
        toast({
          title: "Error",
          description: "Failed to generate QR code",
          variant: "destructive",
        });
      } else {
        const resId = Response.data.message.encrypted_resId;
        const tableId = Response.data.message.encrypted_tableId;
        const delivery_url =
          Response.data.message.restaurant_data[0].delivery_url;
        setImgUrl(Response.data.message.image_url);
        setRestroName(Response.data.message.restaurant_data[0].restaurant_city);
        setRestroImg(Response.data.message.restaurant_data[0].restaurant_image);
        setDeliveryUrl(Response.data.message.restaurant_data[0].delivery_url);
        DownloadQR(resId, tableId, delivery_url);
      }
    } catch (error) {
      console.error("Request error:", error);
      toast({
        title: "Error",
        description: "Network error occurred",
        variant: "destructive",
      });
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const DownloadQR = async (baseUrl, qrCodeUri, delivery_url) => {
    const size = "240";
    const encodedData = encodeURIComponent(
      `${delivery_url}qr_menu/${baseUrl}/${qrCodeUri}`,
    );
    const qrSrc = `https://quickchart.io/qr?text=${encodedData}&size=${size}`;
    setQrImageSrc(qrSrc);
  };
console.log('restaurant name',restroName)
  return (
    <MainLayout>
      <Header
        title="QR Code Generator"
        subtitle={`Generate QR code for ${TableName || "table"}`}
      />

      <div className="p-6 space-y-6">
        {/* Back Button & Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tables
          </Button>

          <div className="flex gap-2 print:hidden">
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={!qrScan || showLoaderAdmin || isDownloading}
              className="gap-2"
            >
              {isDownloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download PNG
                </>
              )}
            </Button>
            <Button
              onClick={handlePrint}
              disabled={!qrScan || showLoaderAdmin}
              className="gap-2 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
            >
              <Printer className="w-4 h-4" />
              Print QR Code
            </Button>
          </div>
        </div>

        {/* QR Card Container */}
        <div className="flex justify-center">
          <div className="w-full max-w-sm">
            {/* Print-friendly card */}
            <Card
              ref={qrRef}
              id="qr-card-print"
              className="relative overflow-hidden border-2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white print:shadow-none print:border-0"
            >
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-teal-500/10 to-transparent rounded-full blur-3xl" />

              {/* Corner accents */}
              <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-amber-400" />
              <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-amber-400" />
              <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-amber-400" />
              <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-amber-400" />

              <CardContent className="relative z-10 p-8 space-y-6">
                {/* Restaurant Logo */}
                <div className="flex flex-col items-center space-y-2">
                  <div className=" flex items-center justify-center shadow-lg " >
                    {restroImg ? (
                      <img
                        src={APL_LINK + imgUrl + restroImg}
                        alt="Restaurant Logo"
                        className=" rounded-full object-cover"
                        crossOrigin="anonymous"
                        style={{width:'200px',height:'200px',marginBottom:'-80px',marginTop:'-80px'}}
                      />
                    ) : (
                      <ChefHat className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-amber-400 tracking-wide">
                    {restroName || "UpRestro"}
                  </h2>
                </div>

                {/* QR Code */}
                {showLoaderAdmin ? (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-gray-300">
                      Generating QR Code...
                    </p>
                  </div>
                ) : qrScan ? (
                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-xl shadow-2xl border-4 border-amber-400">
                      <img
                        src={qrScan}
                        alt="QR Code"
                        className="w-50 h-50 object-contain"
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3 text-gray-400">
                    <QrCodeIcon className="w-16 h-16" />
                    <p className="text-sm">No QR code generated</p>
                  </div>
                )}

                {/* Table Number Badge */}  
                <div className="flex justify-center" style={{marginBottom:"-20px"}}>
                  <div className=" align-items-center gap-1 px-6  mb-0 bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 rounded-full font-bold text-lg  shadow-lg uppercase">
                    <p className="mb-3">Table No {TableName}</p>
                  </div>
                </div>

                {/* Subtitle */}
                <p className="text-center text-gray-200 text-base" style={{marginBottom:"-20px"}}
                >
                  Scan The QR Code
                </p>

                {/* Divider */}
                {/* <div className="flex justify-center">
                  <div className="w-4/5 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
                </div> */}

                {/* Description */}
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold text-amber-400 uppercase">
                    Check Out Our Menu
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Where quality blends with absolute
                    <br />
                    convenience
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Info Text */}
            <div className="mt-4 text-center text-sm text-muted-foreground print:hidden space-y-1">
              <p>Use Ctrl+P or click Print button to print this QR code</p>
              <p className="text-xs">
                Click Download PNG to save the entire card as an image
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 0;
            size: portrait;
          }

          body {
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Hide everything */
          * {
            visibility: hidden !important;
          }

          /* Show only the QR card and its children */
          #qr-card-print,
          #qr-card-print * {
            visibility: visible !important;
          }

          /* Position card in center */
          #qr-card-print {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: 440px !important;
            max-width: 440px !important;
            box-shadow: none !important;
            page-break-inside: avoid !important;
          }

          /* Preserve all colors and gradients */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Hide print button specifically */
          .print\\:hidden {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>
    </MainLayout>
  );
}
